import { ROUND } from "./financeUtils.mjs"
import { isNumber } from "./numberUtils.mjs"
import { replaceASCII } from "./stringUtils.mjs"
import { currenciesSymbols, getLocaleParameters, localesParameters } from "./localeUtils.mjs"

/**
 * Map of currency code to decimal places, built from localesParameters.
 * @type {Object.<string, number>}
 */
const currencyDecimalsMap = {}
for (const params of Object.values(localesParameters)) {
  const code = params.currency?.code
  if (code && !(code in currencyDecimalsMap)) {
    currencyDecimalsMap[code] = params.decimalPlace
  }
}

/**
 * Returns the number of decimal places for a currency based on localesParameters or ISO 4217.
 *
 * @param {string} currencyCode - The ISO 4217 currency code (e.g., "USD", "EUR", "XPF").
 * @returns {number} The number of decimal places for the currency.
 *
 * @example
 * getCurrencyDecimals("EUR"); // 2
 * getCurrencyDecimals("XPF"); // 0
 * getCurrencyDecimals("JPY"); // 0 (from Intl fallback)
 * getCurrencyDecimals("TND"); // 3
 */
function getCurrencyDecimals(currencyCode) {
  if (currencyCode in currencyDecimalsMap) {
    return currencyDecimalsMap[currencyCode]
  }
  // Fallback to Intl for currencies without a defined locale (JPY, XAF, XOF, etc.)
  try {
    return new Intl.NumberFormat("en", { style: "currency", currency: currencyCode }).resolvedOptions().minimumFractionDigits
  } catch {
    return 2
  }
}

const ibanCountryConfig = {
  AD: { format: [4, 4, 4, 4, 4, 4], length: 24 }, // Andorra: ADkk BBBB BBBB CCCC CCCC CCCC
  AE: { format: [4, 4, 4, 4, 3], length: 23 }, // United Arab Emirates: AEkk BBBB BBBB CCCC CCCC CCC
  AT: { format: [4, 4, 4, 4, 4], length: 20 }, // Austria: ATkk BBBB BBBB CCCC CCCC
  AZ: { format: [4, 4, 4, 4, 4, 4, 4], length: 28 }, // Azerbaijan: AZkk BBBB BBBB CCCC CCCC CCCC CCCC
  BA: { format: [4, 4, 4, 4, 4], length: 20 }, // Bosnia and Herzegovina: BAkk BBBS SSCC CCCC CCCC
  BE: { format: [4, 4, 4, 4], length: 16 }, // Belgium: BEkk BBBB CCCC CCCC
  BG: { format: [4, 4, 4, 4, 4, 2], length: 22 }, // Bulgaria: BGkk BBBB SSSS DDCC CCCC CC
  BH: { format: [4, 4, 4, 4, 4, 2], length: 22 }, // Bahrain: BHkk BBBB SSSS CCCC CCCC CC
  BR: { format: [4, 4, 4, 4, 4, 4, 5], length: 29 }, // Brazil: BRkk BBBB BBBB SSSS CCCC CCCC CCCC CC
  CD: { format: [4, 4, 4, 4, 4, 4, 3], length: 27 }, // Congo (Democratic Republic): CDkk BBBB BBBB CCCC CCCC CCCC CCC
  CH: { format: [4, 4, 4, 4, 5], length: 21 }, // Switzerland: CHkk BBBB BBBB CCCC CCCC C
  CR: { format: [4, 4, 4, 4, 5], length: 21 }, // Costa Rica: CRkk BBBB BBBB CCCC CCCC C
  CY: { format: [4, 4, 4, 4, 4, 4, 4], length: 28 }, // Cyprus: CYkk BBBB BBBB CCCC CCCC CCCC CCCC
  CZ: { format: [4, 4, 4, 4, 4, 4], length: 24 }, // Czech Republic: CZkk BBBB SSSS SSCC CCCC CCCC
  DE: { format: [4, 4, 4, 4, 4, 2], length: 22 }, // Germany: DEkk BBBB BBBB CCCC CCCC CC
  DK: { format: [4, 4, 4, 4, 2], length: 18 }, // Denmark: DKkk BBBB CCCC CCCC CC
  DO: { format: [4, 4, 4, 4, 4, 4, 4], length: 28 }, // Dominican Republic: DOkk BBBB BBBB CCCC CCCC CCCC CCC
  EE: { format: [4, 4, 4, 4, 4], length: 20 }, // Estonia: EEkk BBSS CCCC CCCC CCC
  ES: { format: [4, 4, 4, 4, 4, 4], length: 24 }, // Spain: ESkk BBBB GGGG CCCC CCCC CCC
  FI: { format: [4, 4, 4, 4, 2], length: 18 }, // Finland: FIkk BBBB BBCC CCCC CC
  FO: { format: [4, 4, 4, 4, 2], length: 18 }, // Faroe Islands: FOkk BBBB CCCC CCCC CC
  FR: { format: [4, 4, 4, 4, 4, 4, 3], length: 27 }, // France: FRkk BBBB BBBB CCCC CCCC CCCC CCC
  GB: { format: [4, 4, 4, 4, 4, 2], length: 22 }, // United Kingdom: GBkk BBBB SSSS SSCC CCCC CC
  GI: { format: [4, 4, 4, 4, 5], length: 23 }, // Gibraltar: GIkk BBBB XXXX XXXX XXXX XXX
  GL: { format: [4, 4, 4, 4, 2], length: 18 }, // Greenland: GLkk BBBB CCCC CCCC CC
  GQ: { format: [4, 4, 4, 4, 4, 4, 3], length: 27 }, // Equatorial Guinea: GQkk BBBB BBBB CCCC CCCC CCCC CCC
  GR: { format: [4, 4, 4, 4, 4, 4, 3], length: 27 }, // Greece: GRkk BBBB SSSS CCCC CCCC CCCC CCC
  GT: { format: [4, 4, 4, 4, 4, 4, 4], length: 28 }, // Guatemala: GTkk AAAA BBBB CCCC CCCC CCCC CCCC
  GW: { format: [4, 4, 4, 4, 4, 5], length: 25 }, // Guinea-Bissau: GWkk BBBB BBBB CCCC CCCC CCCC
  HR: { format: [4, 4, 4, 4, 5], length: 21 }, // Croatia: HRkk BBBB BBCC CCCC CCCC C
  HU: { format: [4, 4, 4, 4, 4, 4, 4], length: 28 }, // Hungary: HUkk BBBB BBBB CCCC CCCC CCCC CCC
  IE: { format: [4, 4, 4, 4, 4, 2], length: 22 }, // Ireland: IEkk AAAA BBBB BBCC CCCC CC
  IL: { format: [4, 4, 4, 4, 3, 4], length: 23 }, // Israel: ILkk BBBB BBBB CCC CCC CCCC
  IS: { format: [4, 4, 4, 4, 4, 4, 2], length: 26 }, // Iceland: ISkk BBBB SSCC CCCC XXXX XX
  IT: { format: [4, 4, 4, 4, 4, 4, 3], length: 27 }, // Italy: ITkk XAAA AABB BBBB CCCC CCCC CCC
  JO: { format: [4, 4, 4, 4, 4, 4, 6], length: 30 }, // Jordan: JOkk BBBB NNNN CCCC CCCC CCCC CCC
  KW: { format: [4, 4, 4, 4, 4, 4, 6], length: 30 }, // Kuwait: KWkk BBBB NNNN CCCC CCCC CCCC CCC
  KZ: { format: [4, 4, 4, 4, 4], length: 20 }, // Kazakhstan: KZkk BBBB NNNN CCCC CCCC
  LB: { format: [4, 4, 4, 4, 4, 4, 4], length: 28 }, // Lebanon: LBkk BBBB NNNN CCCC CCCC CCCC CCCC
  LI: { format: [4, 4, 4, 4, 5], length: 21 }, // Liechtenstein: LIkk BBBB BBBB CCCC CCCC C
  LT: { format: [4, 4, 4, 4, 4], length: 20 }, // Lithuania: LTkk BBBB BBBB CCCC CCCC
  LU: { format: [4, 4, 4, 4, 4], length: 20 }, // Luxembourg: LUkk BBBB CCCC CCCC CC
  LV: { format: [4, 4, 4, 4, 5], length: 21 }, // Latvia: LVkk BBBB CCCC CCCC CCCC
  MA: { format: [4, 4, 4, 4, 4, 4, 4], length: 28 }, // Morocco: MAkk BBBB CCCC CCCC CCCC CCCC CCC
  MC: { format: [4, 4, 4, 4, 4, 4, 3], length: 27 }, // Monaco: MCkk BBBB BBBB CCCC CCCC CCCC CCC
  MD: { format: [4, 4, 4, 4, 4, 4], length: 24 }, // Moldova: MDkk BBCC CCCC CCCC CCCC CC
  ME: { format: [4, 4, 4, 4, 4], length: 22 }, // Montenegro: MEkk BBBB CCCC CCCC CCCC
  MK: { format: [4, 4, 4, 4, 3], length: 19 }, // North Macedonia: MKkk BBBB CCCC CCCC CC
  MR: { format: [4, 4, 4, 4, 4, 4, 3], length: 27 }, // Mauritania: MRkk BBBB BBBB CCCC CCCC CCCC CCC
  MT: { format: [4, 4, 4, 4, 4, 4, 6], length: 31 }, // Malta: MTkk BBBB SSSS CCCC CCCC CCCC CCC
  MU: { format: [4, 4, 4, 4, 4, 4, 6], length: 30 }, // Mauritius: MUkk BBBB BBSS CCCC CCCC CCCC CCC
  MZ: { format: [4, 4, 4, 4, 4, 5], length: 25 }, // Mozambique: MZkk BBBB BBBB CCCC CCCC CCCC
  NL: { format: [4, 4, 4, 4, 2], length: 18 }, // Netherlands: NLkk BBBB CCCC CCCC CC
  NO: { format: [4, 4, 4, 2], length: 15 }, // Norway: NOkk BBBB CCCC CC
  PK: { format: [4, 4, 4, 4, 4, 4], length: 24 }, // Pakistan: PKkk BBBB CCCC CCCC CCCC CCCC
  PL: { format: [4, 4, 4, 4, 4, 4, 4], length: 28 }, // Poland: PLkk BBBB BBBB CCCC CCCC CCCC CCC
  PS: { format: [4, 4, 4, 4, 4, 4, 5], length: 29 }, // Palestine: PSkk BBBB XXXX XXXX XXXX XXXX XXX
  PT: { format: [4, 4, 4, 4, 4, 5], length: 25 }, // Portugal: PTkk BBBB BBBB CCCC CCCC CCCC
  QA: { format: [4, 4, 4, 4, 4, 4, 5], length: 29 }, // Qatar: QAkk BBBB NNNN CCCC CCCC CCCC CCCC C
  QX: { format: [4, 4, 4, 4, 4, 4, 7], length: 34 }, // Quatar Extended: QXkk BBBB XXXX XXXX XXXX XXXX XXXX XXX
  RO: { format: [4, 4, 4, 4, 5], length: 24 }, // Romania: ROkk BBBB CCCC CCCC CCCC CCCC
  RS: { format: [4, 4, 4, 4, 4], length: 22 }, // Serbia: RSkk BBBB CCCC CCCC CCCC
  SA: { format: [4, 4, 4, 4, 4, 4], length: 24 }, // Saudi Arabia: SAkk BBCC CCCC CCCC CCCC CCCC
  SE: { format: [4, 4, 4, 4, 4, 4], length: 24 }, // Sweden: SEkk BBBB CCCC CCCC CCCC CCCC
  SI: { format: [4, 4, 4, 4, 3], length: 19 }, // Slovenia: SIkk BBBB CCCC CCCC CCC
  SK: { format: [4, 4, 4, 4, 4, 4], length: 24 }, // Slovakia: SKkk BBBB SSSS SSCC CCCC CCCC
  SM: { format: [4, 4, 4, 4, 4, 4, 3], length: 27 }, // San Marino: SMkk XAAA AABB BBBB CCCC CCCC CCC
  TN: { format: [4, 4, 4, 4, 4, 4], length: 24 }, // Tunisia: TNkk BBBB BBBB CCCC CCCC CCCC
  TR: { format: [4, 4, 4, 4, 4, 4, 2], length: 26 }, // Turkey: TRkk BBBB BBCC CCCC CCCC CCCC
}

/**
 * Validates an IBAN (International Bank Account Number) string.
 *
 * The function normalizes the input by removing non-alphanumeric characters,
 * checks the format and length based on country-specific configuration,
 * converts letters to numbers, and verifies the IBAN using the mod-97 algorithm.
 *
 * @param {string} iban - The IBAN string to validate.
 * @returns {boolean} Returns `true` if the input is a valid IBAN, otherwise `false`.
 *
 * @example
 * isValidIban("GB82 WEST 1234 5698 7654 32"); // true
 * isValidIban("DE89370400440532013000"); // true
 * isValidIban("INVALID_IBAN"); // false
 */
function isValidIban(iban) {
  if (!iban || typeof iban !== "string") return false

  const code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/) // match and capture (1) the country code, (2) the check digits, and (3) the rest

  // check syntax and length
  if (!code || iban.length !== ibanCountryConfig[code[1]]?.length) return false

  // rearrange country code and check digits, and convert chars to ints
  const digits = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, letter => letter.charCodeAt(0) - 55)

  // final check
  return mod97(digits) === 1
}

/**
 * Validates a RIB (Relevé d'Identité Bancaire) number for a given country.
 * This function was developed for a moroccan customer therefore for Moroccan RIB only for now.
 * TODO: Upgrade it to make it work for all countries.
 *
 * Specifically checks that the input consists only of digits, validates length for Morocco ("MA"),
 * and verifies the bank key using a modulo 97 checksum calculation.
 *
 * @param {string} input - The RIB number as a string of digits.
 * @param {string} country - The country code (e.g., "MA" for Morocco).
 * @returns {boolean} Returns `true` if the RIB is valid, otherwise `false`.
 *
 * @example
 * isValidRib("123456789012345678901234", "MA"); // true or false depending on validity
 * isValidRib("1234567890123456789012", "FR"); // true or false depending on validity
 * isValidRib("12345ABC", "MA"); // false (contains non-digit characters)
 */
function isValidRib(input, country) {
  if (!/^\d+$/.test(input)) return false
  if (country === "MA" && input.length !== 24) return false
  const accountNumber = input.substring(0, 22)
  const bankKey = input.substring(22)
  const calculatedKey = String(BigInt(97) - ((BigInt(accountNumber) * BigInt(100)) % BigInt(97))).padStart(2, "0")
  return bankKey === calculatedKey
}

/**
 * Computes the modulo 97 checksum of a numeric string.
 *
 * This function processes the string in fragments to handle very large numbers,
 * commonly used in validations such as IBAN.
 *
 * @param {string} string - The numeric string to compute the mod 97 checksum for.
 * @returns {number} The modulo 97 remainder.
 *
 * @example
 * mod97("3214282912345698765432161182"); // Returns a number between 0 and 96
 */
function mod97(string) {
  let checksum = string.slice(0, 2)
  let fragment

  for (let offset = 2; offset < string.length; offset += 7) {
    fragment = String(checksum) + string.substring(offset, offset + 7)
    checksum = parseInt(fragment, 10) % 97
  }

  return checksum
}

/**
 * Rounds a number to the currency decimal places defined by the given locale.
 *
 * @param {number|string} number - The number to round.
 * @param {string} locale - The locale identifier to determine currency decimal places (e.g., "en-US", "fr-FR").
 * @returns {number} The rounded number, with `-0` normalized to `0`.
 *
 * @example
 * roundCurrency(123.456, "en-US"); // 123.46 (assuming 2 decimal places)
 * roundCurrency("99.999", "ja-JP"); // 100 (assuming 0 decimal places)
 * roundCurrency(0, "en-US"); // 0
 */
function roundCurrency(number, locale) {
  if (!number) return number
  number = Number(number)
  const decimalPlace = getLocaleParameters(locale).decimalPlace
  const result = ROUND(number, decimalPlace)
  return result === 0 ? 0 : result // to avoid -0 ("-0 === 0" is true)
}

/**
 * Converts a formatted currency string into a plain number, respecting locale-specific formatting.
 *
 * This function removes currency symbols, codes, thousands separators, and adjusts decimal separators
 * according to the locale, then returns the numeric value.
 *
 * @param {string|any} number - The formatted currency string to unformat.
 * @param {string} locale - The locale identifier used to determine formatting rules (e.g., "en-US", "fr-FR").
 * @returns {number|any} The numeric value of the currency string, or the original input if invalid.
 *
 * @example
 * unformatCurrency("$1,234.56", "en-US"); // 1234.56
 * unformatCurrency("1 234,56 €", "fr-FR"); // 1234.56
 * unformatCurrency("¥1234", "ja-JP"); // 1234
 */
function unformatCurrency(number, locale) {
  if (!number || typeof number !== "string") return number
  number = replaceASCII(number).replace(/\s/g, "")

  const {
    delimiters: { thousands, decimal },
    currency: { symbol, code },
  } = getLocaleParameters(locale)

  // Currency
  number = number.replace(symbol, "").replace(code, "")

  if (decimal !== ".") {
    // Handle "." input in locales using "," as decimal (e.g., fr-FR, de-DE)
    const dotCount = (number.match(/\./g) || []).length
    const commaIndex = number.indexOf(decimal)
    let convertedDotToDecimal = false

    // Detect unambiguous US format: "1,234,567.90" (commas as thousands + dot as decimal)
    // This pattern is clear: multiple commas in thousands positions + dot followed by decimals
    if (dotCount === 1 && commaIndex >= 0 && /^-?\d{1,3}(,\d{3})+\.\d+$/.test(number)) {
      number = number.replace(/,/g, "") // Remove comma thousands
      number = number.replace(".", decimal) // Convert dot to locale decimal for downstream processing
      convertedDotToDecimal = true
    } else if (dotCount === 1 && commaIndex < 0) {
      // Single dot, no comma: determine if dot is decimal or thousands
      const dotIndex = number.indexOf(".")
      const afterDot = number.substring(dotIndex + 1)
      const beforeDot = number.substring(0, dotIndex)

      // Just a dot with no digits: remove it (e.g., "." -> "")
      if (!beforeDot && !afterDot) {
        number = number.replace(".", "")
      } else {
        // Dot is thousands only if: locale uses "." for thousands + exactly "X.000" pattern
        // e.g., "1.000" -> 1000, but "5.555" -> 5.555, "0.123" -> 0.123
        const looksLikeThousands = thousands === "." && /^0{3}$/.test(afterDot) && /^[1-9]\d{0,2}$/.test(beforeDot)

        if (!looksLikeThousands) {
          number = number.replace(".", decimal)
          convertedDotToDecimal = true
        }
      }
    } else if (dotCount > 1) {
      // Multiple dots are thousands separators (e.g., "1.234.567" -> 1234567)
      number = number.replace(/\./g, "")
    }
    // else: both dot and comma present (e.g., "1.234,56") - handled below

    // Handle US format pasted into comma-decimal locale (e.g., "1,234,567")
    // Requires 2+ commas to avoid ambiguity ("5,555" should be 5.555 in French)
    const commaCount = (number.match(/,/g) || []).length
    if (!convertedDotToDecimal && number.indexOf(".") < 0 && commaCount >= 2) {
      if (/^-?\d{1,3}(,\d{3})+$/.test(number)) {
        number = number.replace(/,/g, "")
      }
    }
  }

  // Thousand separators
  let decimals = ""
  if (number.indexOf(decimal) > 0) decimals = number.substr(number.indexOf(decimal), number.length)
  if (decimals) number = number.replace(decimals, "")
  if (number.includes(thousands)) number = number.replace(thousands === "." ? /\./g : new RegExp(thousands, "g"), "")

  // Decimal
  if (decimals) number = number + decimals
  number = number.replace(decimal, ".")

  return Number(number)
}

/**
 * Formats a number as a currency string according to locale and formatting options.
 * See [Intl.NumberFormat()](https://v8.dev/features/intl-numberformat) for details regarding the option field.
 *
 * Supports locale-specific formatting rules, currency symbols, grouping, and decimal places.
 * Special handling is included for certain locales with unique formatting quirks.
 *
 * @param {number|string|null|undefined} number - The number to format.
 * @param {string} locale - The locale identifier (e.g., "en-US", "fr-FR").
 * @param {object|string|number} [options] - Formatting options or currency code string,
 *   or `0` to specify no decimal digits.
 *   - If `0`, sets minimum and maximum fraction digits to 0.
 *   - If a string, treated as the currency code.
 *   - If an object, passed as options to `Intl.NumberFormat`.
 * @returns {string|number|null|undefined} The formatted currency string, or the original input if invalid.
 *
 * @example
 * formatCurrency(1234.56, "en-US"); // "$1,234.56"
 * formatCurrency(1234.56, "fr-FR", "EUR"); // "1 234,56 €"
 * formatCurrency(1000, "en-US", 0); // "$1,000"
 * formatCurrency(null, "en-US"); // null
 */
function formatCurrency(number, locale, options) {
  if (!number && number !== 0) return number

  // this is a hack to make it simpler when we want no digits
  //TODO : bug on option mutation
  if (isNumber(options)) options = { minimumFractionDigits: options, maximumFractionDigits: options }
  else if (typeof options === "string") options = { currency: options }
  else if (!options) options = {}

  const hasStyleNone = options.style === "none"

  const localeParameters = getLocaleParameters(locale)
  if (!options?.currency) options.currency = localeParameters.currency.code
  options.style = hasStyleNone ? undefined : "currency"
  if (localeParameters.useGrouping) options.useGrouping = true
  if (options.maximumFractionDigits === undefined && options.minimumFractionDigits === undefined) {
    options.minimumFractionDigits = localeParameters.decimalPlace
    options.maximumFractionDigits = options.minimumFractionDigits
  }

  // The symbol "FCFP" comes from Js native API Intl.NumberFormat(locale, options).format(number) for Franc Pacifique
  // FCFP (or F.CFP) is the official symbol used in daily life in those territories
  // XPF is the ISO code, used in banking contexts, wire transfers, and computer systems
  if (["fr-PF", "fr-NC", "fr-WF"].includes(locale) || options.currency === "XPF") {
    options.currencyDisplay = "code"
  }

  // avoid -0.00
  if (Object.is(Math.round(number * 10 ** (options.minimumFractionDigits || 2)), -0)) number = 0

  let formattedCurrency = new Intl.NumberFormat(locale, options).format(number)

  if (["fr-GN", "lt-LT", "fr-BE"].includes(locale)) {
    formattedCurrency = replaceASCII(formattedCurrency)
    if (hasStyleNone) formattedCurrency = formattedCurrency.replace(/ /g, ".")
    else formattedCurrency = formattedCurrency.replace(/[ ](?=.*[ ])/g, ".")
  }
  if (["en-LT", "en-BE"].includes(locale)) {
    formattedCurrency = formattedCurrency.replace(new RegExp(/,/g), "#").replace(new RegExp(/\./), ",").replace(new RegExp(/#/g), ".")
  }
  if (locale === "mx-MX") {
    formattedCurrency = formattedCurrency.replace("MX", "")
  }

  return formattedCurrency
}

/**
 * @deprecated Use "formatCurrency()" instead. This function will be removed in future versions.
 *
 * Formats a number as currency using either a locale or a currency code.
 *
 * If the `currencyCodeOrLocale` parameter contains a hyphen (`-`), it is treated as a locale identifier
 * and formatting is done accordingly. Otherwise, it is treated as a currency code and the number
 * is formatted using the default locale with that currency.
 *
 * @param {number|string|null|undefined} number - The number to format.
 * @param {string} currencyCodeOrLocale - A locale identifier (e.g., "en-US") or a currency code (e.g., "USD").
 * @param {object} [options={}] - Optional formatting options passed to the underlying formatter.
 * @returns {string|number|null|undefined} The formatted currency string, or the original input if invalid.
 *
 * @example
 * getFormattedCurrency(1234.56, "en-US"); // "$1,234.56"
 * getFormattedCurrency(1234.56, "EUR"); // "€1,234.56" (assuming default locale)
 */
function getFormattedCurrency(number, currencyCodeOrLocale, options = {}) {
  if (currencyCodeOrLocale?.includes("-")) {
    return formatCurrency(number, currencyCodeOrLocale, options)
  }

  options.currency = currencyCodeOrLocale //TODO : bug on option mutation
  return formatCurrency(number, undefined, options)
}

/**
 * Retrieves the currency symbol or code for a given locale and currency code.
 *
 * Returns the symbol by default, or the currency code if `options.currencyDisplay` is set to `"code"`.
 * Falls back to locale defaults if the currency code is not found.
 *
 * @param {string} locale - The locale identifier (e.g., "en-US", "fr-FR").
 * @param {string} currencyCode - The ISO currency code (e.g., "USD", "EUR").
 * @param {object} [options={}] - Optional settings.
 * @param {string} [options.currencyDisplay] - If `"code"`, returns the currency code instead of symbol.
 * @returns {string|undefined} The currency symbol or code based on parameters.
 *
 * @example
 * getCurrencySymbol("en-US", "USD"); // "$"
 * getCurrencySymbol("fr-FR", "EUR"); // "€"
 * getCurrencySymbol("en-US", "USD", { currencyDisplay: "code" }); // "USD"
 */
function getCurrencySymbol(locale, currencyCode, options = {}) {
  const codeDisplay = options.currencyDisplay === "code"
  const { symbol: localeCurrencySymbol, code } = getLocaleParameters(locale).currency || {}
  const currencyFromCode = currenciesSymbols[currencyCode]
  return currencyCode
    ? codeDisplay
      ? currencyFromCode?.code || code
      : currencyFromCode?.symbol || localeCurrencySymbol
    : codeDisplay
      ? code
      : localeCurrencySymbol
}

/**
 * Converts a localized numeric string into a JavaScript number.
 *
 * This function removes locale-specific thousands separators, currency symbols,
 * spaces, and replaces the locale decimal delimiter with a dot, then parses the result.
 * If the input is not a valid number after these transformations, the original string is returned.
 *
 * @param {string} string - The localized numeric string to convert.
 * @param {string} locale - The locale identifier (e.g., "en-US", "fr-FR").
 * @returns {number|string} The parsed number if valid, otherwise the original input string.
 *
 * @example
 * coerceStringToJsNumber("1,234.56", "en-US"); // 1234.56
 * coerceStringToJsNumber("1 234,56 €", "fr-FR"); // 1234.56
 * coerceStringToJsNumber("abc", "en-US"); // "abc" (not a number)
 */
function coerceStringToJsNumber(string, locale) {
  if (typeof string !== "string" || string === "") return string
  const { delimiters } = getLocaleParameters(locale)
  const localeDecimalDelimiter = delimiters.decimal
  const localeThousandsDelimiter = delimiters.thousands
  const currencySymbol = getCurrencySymbol(locale)
  const replaceRegex = new RegExp(
    `\\s|%${currencySymbol ? `|${currencySymbol}` : ""}${localeThousandsDelimiter ? `|\\${localeThousandsDelimiter}` : ""}`,
    "g",
  )
  const lintedString = string.replace(replaceRegex, "").replace(localeDecimalDelimiter, ".")
  return isNaN(lintedString) ? string : parseFloat(lintedString)
}

/**
 * Formats an IBAN string according to the country-specific grouping pattern.
 *
 * Removes any existing formatting, validates the IBAN length for the country,
 * and inserts spaces to separate groups of characters as defined by the country's IBAN format.
 *
 * @param {string} iban - The IBAN string to format.
 * @returns {string} The formatted IBAN with spaces separating groups, or the original input if invalid.
 *
 * @example
 * formatIban("GB82WEST12345698765432"); // "GB82 WEST 1234 5698 7654 32"
 * formatIban("DE89370400440532013000"); // "DE89 3704 0044 0532 0130 00"
 */
function formatIban(iban) {
  const unformattedIban = unformatIban(iban)
  // Extract the country code from the IBAN
  const countryCode = unformattedIban.slice(0, 2).toUpperCase()

  // Get the format for the specific country
  const ibanConfig = ibanCountryConfig[countryCode]

  // Check if the format and length are valid
  if (!ibanConfig || ibanConfig.length !== unformattedIban.length) return iban

  let formattedIban = ""
  let index = 0

  // Apply the format
  ibanConfig.format.forEach(groupSize => {
    formattedIban += " " + unformattedIban.slice(index, index + groupSize)
    index += groupSize
  })

  return formattedIban.trim()
}

/**
 * Removes all whitespace characters from an IBAN string.
 *
 * @param {string} iban - The IBAN string to unformat.
 * @returns {string} The IBAN string without any spaces.
 *
 * @example
 * unformatIban("GB82 WEST 1234 5698 7654 32"); // "GB82WEST12345698765432"
 */
function unformatIban(iban) {
  return iban.replace(/\s/g, "")
}

export {
  coerceStringToJsNumber,
  formatCurrency,
  formatIban,
  getCurrencyDecimals,
  getCurrencySymbol,
  getFormattedCurrency,
  ibanCountryConfig,
  isValidIban,
  isValidRib,
  mod97,
  roundCurrency,
  unformatCurrency,
  unformatIban,
}
